import { useEffect, useRef, useState } from 'react';
import { BiReport } from '@app/model/bi/Bi';
import { useBiColumns, useSaveBiColumns } from '@app/model/bi/service/BiHooks';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { chosenOf, ColumnBlock, legacyOf, listOf, toggled } from './ColumnSets.utilities';

export type { ColumnBlock } from './ColumnSets.utilities';

const readLegacy = (key: string): string | null => {
	try { return window.localStorage.getItem(key); } catch { return null; }
};
const removeLegacy = (key: string) => {
	try { window.localStorage.removeItem(key); } catch { /* the move runs again at the next visit */ }
};

// D11 (grill of the Channels report) and ROO2-283: one Columns hook for every BI report. The user account keeps the choice
// of each report (D22, D24), the same on every device. Until the account answers, and when it cannot be read, the page
// shows the choice of this browser from before ROO2-283 (legacyKey) or the defaults. A user with no choice in the account
// and one in the browser gets it moved to the account once; the browser key goes after the save. A failed save keeps the
// change for this visit and says so.
const useColumnSets = <T extends ColumnBlock>(report: BiReport, blocks: readonly T[], defaults: readonly T[], legacyKey?: string)
	: [T[], (block: T) => void] => {
	const saved = useBiColumns(report);
	const save = useSaveBiColumns(report);
	const toaster = useToaster();
	const [legacy] = useState(() => legacyKey === undefined ? null : legacyOf(readLegacy(legacyKey), blocks));
	const moved = useRef(false);

	useEffect(() => {
		if (legacyKey === undefined || legacy === null || saved.data !== null || moved.current) return;
		moved.current = true;
		save.mutate(listOf(legacy, blocks), { onSuccess: () => removeLegacy(legacyKey) });
	}, [saved.data, legacy, legacyKey, blocks, save]);

	const chosen = saved.data === undefined || saved.data === null ? (legacy ?? chosenOf(null, blocks, defaults)) : chosenOf(saved.data.columns, blocks, defaults);
	const toggle = (block: T) => save.mutate(listOf(toggled(chosen, block, blocks), blocks),
		{ onError: () => toaster.error('The columns were not saved', 'Try again. The table keeps your change for this visit.') });
	return [chosen, toggle];
};

export default useColumnSets;
